import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toast';
import { ErrorState, Spinner } from '@/components/ui/states';
import { AppRoutes } from '@/app/routes';
import { SessionProvider } from '@/app/providers/session-provider';
import { PhoneSystemProvider, usePhoneSystemHealth } from '@/app/providers/phone-system-provider';
import { PhoneSystemNotice } from '@/app/shell/phone-system-notice';
import { ThemeProvider } from '@/app/providers/theme-provider';
import LoginPage from '@/pages/auth/login-page';
import LogoutPage from '@/pages/auth/logout-page';
import { queryClient } from '@/lib/query';

/** Django serves the SPA under /admin/. */
const BASENAME = '/admin';

function BootScreen({
  loading,
  error,
  retry,
}: {
  loading: boolean;
  error: unknown;
  retry: () => void;
}) {
  const phoneSystem = usePhoneSystemHealth();
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="size-6" />
        <span className="sr-only">Loading the console…</span>
      </div>
    );
  }
  if (phoneSystem?.backend_healthy === false) {
    return <PhoneSystemNotice health={phoneSystem} fullScreen onRetry={retry} />;
  }
  return (
    <div className="flex h-full items-center justify-center">
      <ErrorState error={error} onRetry={retry} />
    </div>
  );
}

function AuthenticatedApp() {
  return (
    <PhoneSystemProvider>
      <SessionProvider fallback={BootScreen}>
        <AppRoutes />
      </SessionProvider>
    </PhoneSystemProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        {/* Instant, like the Django popovers these replaced: on these dense
            list pages the tooltip is the primary way to read a row, not a
            secondary hint, so a delay reads as lag. */}
        <TooltipProvider delayDuration={0} skipDelayDuration={0}>
          <RouterProvider router={router} />
          <Toaster />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

// A data router supports the contact editor's navigation blocker, including
// browser Back/Forward. Existing nested routes and authentication stay intact.
const router = createBrowserRouter([
  { path: 'login', element: <LoginPage /> },
  { path: 'logout', element: <LogoutPage /> },
  { path: '*', element: <AuthenticatedApp /> },
], { basename: BASENAME });
